import { useState, useEffect, useCallback } from 'react'
import type { DatabaseConnection } from '@/stores/connectionStore'
import { useToast } from '@/hooks/useToast'
import { AgentProxyService } from '@/services/agentProxyService'
import { ConfirmationModal } from '@/components/common/ConfirmationModal'

interface Event {
  name: string
  event_type?: string
  status?: string
  definer?: string
  time_zone?: string
  on_completion?: string
  execute_at?: string
  interval_value?: string
  interval_field?: string
  starts?: string
  ends?: string
  event_comment?: string
  event_body?: string
}

interface EventDetailsViewProps {
  connection: DatabaseConnection
  database: string
  eventName: string
  schema?: string
}

export function EventDetailsView({ connection, database, eventName }: EventDetailsViewProps) {
  const [event, setEvent] = useState<Event | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const { showToast } = useToast()

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        setIsLoading(true)
        const eventDetails = await AgentProxyService.getEventDetails(
          connection.id,
          database,
          eventName
        )
        let parsed: Event | null = null
        if (eventDetails && typeof eventDetails === 'object' && !Array.isArray(eventDetails)) {
          const obj: Record<string, unknown> = eventDetails as Record<string, unknown>
          parsed = {
            name: typeof obj.name === 'string' ? obj.name : eventName,
            event_type: typeof obj.event_type === 'string' ? obj.event_type : undefined,
            status: typeof obj.status === 'string' ? obj.status : undefined,
            definer: typeof obj.definer === 'string' ? obj.definer : undefined,
            time_zone: typeof obj.time_zone === 'string' ? obj.time_zone : undefined,
            on_completion: typeof obj.on_completion === 'string' ? obj.on_completion : undefined,
            execute_at: typeof obj.execute_at === 'string' ? obj.execute_at : undefined,
            interval_value: typeof obj.interval_value === 'string' ? obj.interval_value : undefined,
            interval_field: typeof obj.interval_field === 'string' ? obj.interval_field : undefined,
            starts: typeof obj.starts === 'string' ? obj.starts : undefined,
            ends: typeof obj.ends === 'string' ? obj.ends : undefined,
            event_comment: typeof obj.event_comment === 'string' ? obj.event_comment : undefined,
            event_body: typeof obj.event_body === 'string' ? obj.event_body : undefined,
          }
        }
        setEvent(parsed)
      } catch (error) {
        console.error('Failed to fetch event details:', error)
        showToast('error', 'Failed to load event details')
      } finally {
        setIsLoading(false)
      }
    }

    void fetchEvent()
  }, [connection.id, database, eventName, showToast])

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }

  const handleDeleteConfirm = useCallback(async () => {
    try {
      setIsDeleting(true)
      await AgentProxyService.dropEvent(connection.id, database, eventName)
      showToast('success', `Event "${eventName}" dropped successfully`)
      // TODO: Navigate back to events list or close detail view
    } catch (error) {
      console.error('Failed to drop event:', error)
      showToast('error', 'Failed to drop event')
    } finally {
      setIsDeleting(false)
    }
  }, [connection.id, database, eventName, showToast])

  const handleCloseDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(false)
  }, [])

  const handleConfirmDelete = useCallback(() => {
    void handleDeleteConfirm()
  }, [handleDeleteConfirm])

  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return 'N/A'
    try {
      return new Date(dateTime).toLocaleString()
    } catch {
      return dateTime
    }
  }

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'ENABLE':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'DISABLE':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'DISABLE ON SLAVE':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
      case undefined:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!event) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="text-6xl mb-4">❌</div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Event Not Found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            The event "{eventName}" could not be found.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 h-full overflow-auto">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="text-3xl mr-4">⏰</div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {event.name}
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  MySQL Scheduled Event • {event.event_type || 'Unknown Type'}
                </p>
              </div>
            </div>
            
            <button type="button"
              onClick={handleDeleteClick}
              disabled={isDeleting}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Dropping...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Drop Event
                </>
              )}
            </button>
          </div>
        </div>

        {/* Event Properties */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Event Properties
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Event Type
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {event.event_type || 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Status
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                  {event.status || 'N/A'}
                </span>
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Definer
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {event.definer || 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                Time Zone
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {event.time_zone || 'N/A'}
              </p>
            </div>
            
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                On Completion
              </label>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                {event.on_completion || 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Timing Information */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Timing Information
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {event.event_type === 'ONE TIME' && event.execute_at && (
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg lg:col-span-2">
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                  Execute At
                </label>
                <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                  {formatDateTime(event.execute_at)}
                </p>
              </div>
            )}
            
            {event.event_type === 'RECURRING' && (
              <>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Interval
                  </label>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                    {event.interval_value ? `${event.interval_value} ${event.interval_field}` : 'N/A'}
                  </p>
                </div>
                
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Starts
                  </label>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                    {formatDateTime(event.starts || undefined)}
                  </p>
                </div>
                
                {event.ends && (
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg lg:col-span-2">
                    <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                      Ends
                    </label>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white mt-1">
                      {formatDateTime(event.ends)}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Comment */}
        {event.event_comment && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Comment
            </h2>
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
              {event.event_comment}
            </p>
          </div>
        )}

        {/* Event Body */}
        {event.event_body && (
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Event Definition
            </h2>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 overflow-x-auto">
              <pre className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap font-mono">
                {event.event_body}
              </pre>
            </div>
          </div>
        )}

        {/* Information */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-3">
            About MySQL Events
          </h3>
          <div className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
            <p>• MySQL events are scheduled tasks that run automatically at specified times</p>
            <p>• One-time events execute only once at a specified date/time</p>
            <p>• Recurring events repeat at regular intervals</p>
            <p>• Events can be enabled, disabled, or disabled on slave servers</p>
            <p>• The event scheduler must be enabled for events to execute</p>
          </div>
        </div>
      </div>
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        onConfirm={handleConfirmDelete}
        title="Drop Event"
        message={`Are you sure you want to drop the event "${eventName}"? This action cannot be undone.`}
        confirmText="Drop Event"
        cancelText="Cancel"
        variant="danger"
        isDangerous={true}
      />
    </div>
  )
}
